import React, { useState, useCallback } from 'react';
import { Empty } from 'antd';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePW9324Query } from './hooks/usePW9324Query';
import { usePW9324Lookup } from './hooks/usePW9324Lookup';
import QueryFilterPanel from './components/QueryFilterPanel';
import LeaveSummaryTable from './components/LeaveSummaryTable';
import type { QueryFilterValues } from './components/QueryFilterPanel';
import './PW9324Form.css';

const DEFAULT_YEAR = new Date().getFullYear();

const PW9324Form: React.FC = () => {
  const { loading, queried, noData, leaveTypes, data, count, runQuery, reset } =
    usePW9324Query();
  const { deptFromName, deptToName, lookupDeptFrom, lookupDeptTo } =
    usePW9324Lookup();

  const [filters, setFilters] = useState<QueryFilterValues>({
    year: DEFAULT_YEAR,
    deptFrom: '',
    deptTo: '',
  });

  const handleChange = useCallback(
    (field: keyof QueryFilterValues, value: string | number) => {
      setFilters(prev => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleDeptFromBlur = useCallback(() => {
    lookupDeptFrom(filters.deptFrom);
  }, [filters.deptFrom, lookupDeptFrom]);

  const handleDeptToBlur = useCallback(() => {
    lookupDeptTo(filters.deptTo);
  }, [filters.deptTo, lookupDeptTo]);

  const handleQuery = useCallback(() => {
    runQuery({
      year: filters.year,
      dept_from: filters.deptFrom.trim() || undefined,
      dept_to: filters.deptTo.trim() || undefined,
    });
  }, [filters, runQuery]);

  const handleReset = useCallback(() => {
    setFilters({ year: DEFAULT_YEAR, deptFrom: '', deptTo: '' });
    reset();
  }, [reset]);

  const handlePrint = useCallback(() => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;

    const deptRange =
      filters.deptFrom || filters.deptTo
        ? `${filters.deptFrom || '(全部)'}～${filters.deptTo || '(全部)'}`
        : '全部部門';

    const headerRow = [
      '員工代號',
      '姓名',
      '部門',
      ...leaveTypes.map(lt => lt.name),
      '合計天數',
      '合計時數',
    ]
      .map(h => `<th>${h}</th>`)
      .join('');

    const bodyRows = data
      .map(row => {
        const leaveCells = leaveTypes.map((_, idx) => {
          const qty = row.leave_qty?.[idx + 1] ?? 0;
          return `<td class="right">${qty !== 0 ? qty.toFixed(1) : ''}</td>`;
        });
        return `<tr>
          <td>${row.emp_code}</td>
          <td>${row.emp_name}</td>
          <td>${row.dept_name}</td>
          ${leaveCells.join('')}
          <td class="right">${row.d_tot != null ? row.d_tot.toFixed(2) : ''}</td>
          <td class="right">${row.h_tot != null ? row.h_tot.toFixed(1) : ''}</td>
        </tr>`;
      })
      .join('');

    win.document.write(`<!DOCTYPE html><html><head>
<meta charset="utf-8" />
<title>${filters.year} 年度請假總表</title>
<style>
  body { font-family: 'Microsoft JhengHei', sans-serif; margin: 20px; font-size: 11px; }
  h2 { text-align: center; font-size: 14px; margin-bottom: 4px; }
  p.sub { text-align: center; font-size: 11px; margin: 0 0 12px; color: #555; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #333; padding: 2px 5px; }
  th { background: #f0f0f0; text-align: center; }
  .right { text-align: right; }
  @media print { body { margin: 10mm; } }
</style></head><body>
<h2>${filters.year} 年度請假總表</h2>
<p class="sub">部門範圍：${deptRange}　共 ${count} 人</p>
<table>
  <thead><tr>${headerRow}</tr></thead>
  <tbody>${bodyRows}</tbody>
</table>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  }, [data, leaveTypes, filters, count]);

  return (
    <PermissionGuard module={Modules.PW9324 ?? 'pw9324f1'} action="query">
      <div className="pw9324-form">
        <div className="page-wrapper">
          <h2 className="page-title">年度請假總表</h2>

          <QueryFilterPanel
            values={filters}
            deptFromName={deptFromName}
            deptToName={deptToName}
            loading={loading}
            onChange={handleChange}
            onDeptFromBlur={handleDeptFromBlur}
            onDeptToBlur={handleDeptToBlur}
            onQuery={handleQuery}
            onReset={handleReset}
          />

          {queried && noData && (
            <Empty description="查無此資料" style={{ marginTop: 40 }} />
          )}

          {queried && !noData && (
            <LeaveSummaryTable
              leaveTypes={leaveTypes}
              data={data}
              count={count}
              loading={loading}
              year={filters.year}
              deptFrom={filters.deptFrom}
              deptTo={filters.deptTo}
              onPrint={handlePrint}
            />
          )}
        </div>
      </div>
    </PermissionGuard>
  );
};

export default PW9324Form;
